import { Dispatch, SetStateAction, useCallback } from "react";

import { useGetOutboxCampaignOptionsQuery } from "@/pages/Private/redux/list/list-outbox.api";

import { campaignIdsForLists } from "./outboxCampaignFilter";

import type { OutboxCampaignOption } from "../../../List/schema/outbox-counts";
import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface UseOutboxCampaignScopeArgs {
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	setSelectedLists: Dispatch<SetStateAction<CustomListsAutocompleteForSequencerItem[]>>;
	setSelectedCampaignIds: Dispatch<SetStateAction<string[]>>;
}

interface OutboxCampaignScope {
	/** The campaign filter's options for the selected lists (the last answer while a new one loads). */
	campaignOptions: OutboxCampaignOption[] | undefined;
	/** Applies a new list selection and, in the same step, drops the campaigns it removes. */
	applyLists: (lists: CustomListsAutocompleteForSequencerItem[]) => void;
}

/**
 * The Outbox's campaign filter options and the list change that keeps the campaign filter on the
 * selected lists (PREC-4291). The options for the lists selected before the change (`currentData`,
 * never an older answer) place each selected campaign on its list.
 */
export const useOutboxCampaignScope = ({
	selectedLists,
	setSelectedLists,
	setSelectedCampaignIds,
}: UseOutboxCampaignScopeArgs): OutboxCampaignScope => {
	const { data: campaignOptions, currentData } = useGetOutboxCampaignOptionsQuery({
		listIds: selectedLists.map((list) => list.id as number),
	});

	const applyLists = useCallback(
		(lists: CustomListsAutocompleteForSequencerItem[]) => {
			setSelectedCampaignIds((ids) => campaignIdsForLists(ids, currentData, lists));
			setSelectedLists(lists);
		},
		[currentData, setSelectedCampaignIds, setSelectedLists]
	);

	return { campaignOptions, applyLists };
};
